import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  CompleteHarnessOAuthAuthorizationRequest,
  ConnectHarnessCredentialRequest,
  ConnectHarnessCredentialResponse,
  CreateHarnessOAuthAuthorizationResponse,
  HarnessCredential,
  HarnessCredentialProvider,
  HarnessCredentialsResponse,
  HarnessOAuthAuthorization,
  HarnessOAuthAuthorizationResult,
  HarnessOAuthAuthorizationResultResponse,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  HARNESS_CREDENTIALS: {
    LIST: "/api/v2/org/:organization_id/harness-credentials",
    CONNECT: "/api/v2/org/:organization_id/harness-credentials/:provider",
    REVOKE:
      "/api/v2/org/:organization_id/harness-credentials/:harness_credential_id",
    USER_LIST: "/api/v2/org/:organization_id/user/:user_id/harness-credentials",
    USER_CONNECT:
      "/api/v2/org/:organization_id/user/:user_id/harness-credentials/:provider",
    USER_REVOKE:
      "/api/v2/org/:organization_id/user/:user_id/harness-credentials/:harness_credential_id",
    OAUTH_AUTHORIZATIONS:
      "/api/v2/org/:organization_id/user/:user_id/harness-oauth-authorizations",
    OAUTH_AUTHORIZATION:
      "/api/v2/org/:organization_id/user/:user_id/harness-oauth-authorizations/:authorization_id",
    OAUTH_AUTHORIZATION_COMPLETE:
      "/api/v2/org/:organization_id/user/:user_id/harness-oauth-authorizations/:authorization_id/complete",
  },
};

export class HarnessCredentialsAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  // Organization Harness Credential Methods

  /**
   * Get the organization's connected credentials (org scope only).
   */
  async list(config?: AxiosRequestConfig): Promise<HarnessCredential[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<HarnessCredentialsResponse>(
      route(ENDPOINTS.HARNESS_CREDENTIALS.LIST, {
        organization_id: orgId || "",
      }),
      config,
    );

    return response.data.data.harness_credentials;
  }

  /**
   * Connect an org-scoped credential, replacing whatever the organization had for this provider.
   */
  async connect(
    provider: HarnessCredentialProvider,
    data: ConnectHarnessCredentialRequest,
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential> {
    const orgId = await this.client.getOrgID();

    const response =
      await this.client.axios.post<ConnectHarnessCredentialResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.CONNECT, {
          organization_id: orgId || "",
          provider,
        }),
        data,
        config,
      );

    return response.data.data.harness_credential;
  }

  /**
   * Revoke an org-scoped credential.
   */
  async revoke(
    harnessCredentialId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.HARNESS_CREDENTIALS.REVOKE, {
        organization_id: orgId || "",
        harness_credential_id: harnessCredentialId,
      }),
      config,
    );
  }

  // User Harness Credential Methods

  /**
   * Get what the current user's harness turns resolve to: their own credentials plus the
   * organization's, which they inherit for any provider they have not connected themselves.
   */
  async listUserHarnessCredentials(
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<HarnessCredentialsResponse>(
      route(ENDPOINTS.HARNESS_CREDENTIALS.USER_LIST, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      config,
    );

    return response.data.data.harness_credentials;
  }

  /**
   * Connect a credential for the current user, taking precedence over the organization's.
   */
  async connectUserHarnessCredential(
    provider: HarnessCredentialProvider,
    data: ConnectHarnessCredentialRequest,
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.post<ConnectHarnessCredentialResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.USER_CONNECT, {
          organization_id: orgId || "",
          user_id: userId || "",
          provider,
        }),
        data,
        config,
      );

    return response.data.data.harness_credential;
  }

  /**
   * Revoke one of the current user's credentials. Their turns fall back to the organization's.
   */
  async revokeUserHarnessCredential(
    harnessCredentialId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.HARNESS_CREDENTIALS.USER_REVOKE, {
        organization_id: orgId || "",
        user_id: userId || "",
        harness_credential_id: harnessCredentialId,
      }),
      config,
    );
  }

  // Subscription Sign-in Methods

  /**
   * Start a subscription sign-in. The returned `mode` says which of the two follow-ups finishes it:
   * `paste_code` takes a code from the user, `device_code` gives the user one and is finished by
   * polling.
   */
  async createOAuthAuthorization(
    provider: HarnessCredentialProvider,
    config?: AxiosRequestConfig,
  ): Promise<HarnessOAuthAuthorization> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.post<CreateHarnessOAuthAuthorizationResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.OAUTH_AUTHORIZATIONS, {
          organization_id: orgId || "",
          user_id: userId || "",
        }),
        { provider },
        config,
      );

    return response.data.data.authorization;
  }

  /**
   * Finish a `paste_code` sign-in. Send the whole code the provider showed — the server splits it.
   */
  async completeOAuthAuthorization(
    authorizationId: string,
    data: CompleteHarnessOAuthAuthorizationRequest,
    config?: AxiosRequestConfig,
  ): Promise<HarnessOAuthAuthorizationResult> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.post<HarnessOAuthAuthorizationResultResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.OAUTH_AUTHORIZATION_COMPLETE, {
          organization_id: orgId || "",
          user_id: userId || "",
          authorization_id: authorizationId,
        }),
        data,
        config,
      );

    return {
      authorization: response.data.data.authorization,
      harnessCredential: response.data.data.harness_credential,
    };
  }

  /**
   * Check a sign-in. On a `device_code` flow each call is also the poll: the server asks the
   * provider once, and the call that finds the user has approved returns the credential.
   */
  async getOAuthAuthorization(
    authorizationId: string,
    config?: AxiosRequestConfig,
  ): Promise<HarnessOAuthAuthorizationResult> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.get<HarnessOAuthAuthorizationResultResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.OAUTH_AUTHORIZATION, {
          organization_id: orgId || "",
          user_id: userId || "",
          authorization_id: authorizationId,
        }),
        config,
      );

    return {
      authorization: response.data.data.authorization,
      harnessCredential: response.data.data.harness_credential,
    };
  }
}
